<!-- src/apps/secret/views/docs/JsonSchemaView.vue -->

<script setup lang="ts">
  /**
   * Legible, dependency-free JSON Schema renderer for the API reference.
   *
   * The OpenAPI specs are generated with every schema INLINED (no $ref), so
   * this component only has to walk plain JSON Schema nodes — object
   * properties, array items, anyOf/oneOf unions, enums — and print them as a
   * compact indented tree in the SeuID terminal style. It deliberately does
   * NOT try to be a full JSON Schema validator/renderer (that is what heavy
   * third-party viewers do); it favours readability and a tiny footprint.
   *
   * Recursion is bounded by MAX_DEPTH so a pathological/cyclic-looking schema
   * can never blow the render up.
   */
  import { computed } from 'vue';

  type Schema = Record<string, unknown> & {
    type?: string | string[];
    properties?: Record<string, Schema>;
    required?: string[];
    items?: Schema;
    anyOf?: Schema[];
    oneOf?: Schema[];
    allOf?: Schema[];
    enum?: unknown[];
    const?: unknown;
    description?: string;
    format?: string;
  };

  const props = withDefaults(
    defineProps<{
      schema: Schema;
      name?: string;
      required?: boolean;
      depth?: number;
    }>(),
    { name: '', required: false, depth: 0 }
  );

  const MAX_DEPTH = 7;

  const isObject = (v: unknown): v is Schema =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

  /** Human-readable one-line type for a schema node. */
  function typeLabel(s: Schema | undefined): string {
    if (!isObject(s)) return 'any';
    if (Array.isArray(s.enum)) return 'enum';
    if (s.const !== undefined) return 'const';
    const union = s.anyOf ?? s.oneOf;
    if (Array.isArray(union)) {
      const members = union.map((m) => typeLabel(m));
      return [...new Set(members)].join(' | ');
    }
    if (Array.isArray(s.allOf)) return 'object';
    if (Array.isArray(s.type)) return s.type.join(' | ');
    if (s.type === 'array') return `array<${typeLabel(s.items)}>`;
    if (typeof s.type === 'string') {
      return s.format ? `${s.type} <${s.format}>` : s.type;
    }
    if (isObject(s.properties)) return 'object';
    return 'any';
  }

  const label = computed(() => typeLabel(props.schema));

  const description = computed(() =>
    typeof props.schema?.description === 'string' ? props.schema.description : ''
  );

  const enumValues = computed(() => {
    const e = props.schema?.enum;
    return Array.isArray(e) ? e : null;
  });

  const constValue = computed(() =>
    props.schema?.const !== undefined ? props.schema.const : undefined
  );

  // Object properties to expand: the node's own `properties`, merged with the
  // properties contributed by any `allOf` branches (later branches win). This
  // is what lets an allOf-composed schema render its fields instead of an
  // opaque `object`. Shallow merge — enough for the generated specs.
  const objectProperties = computed<Record<string, Schema> | null>(() => {
    const s = props.schema;
    const merged: Record<string, Schema> = isObject(s?.properties)
      ? { ...(s.properties as Record<string, Schema>) }
      : {};
    if (Array.isArray(s?.allOf)) {
      for (const branch of s.allOf) {
        if (isObject(branch) && isObject(branch.properties)) {
          Object.assign(merged, branch.properties as Record<string, Schema>);
        }
      }
    }
    return Object.keys(merged).length ? merged : null;
  });

  // Required keys, likewise unioned across the node and its allOf branches.
  const requiredKeys = computed<string[]>(() => {
    const s = props.schema;
    const own = Array.isArray(s?.required) ? (s.required as string[]) : [];
    const fromAllOf = Array.isArray(s?.allOf)
      ? s.allOf.flatMap((b) => (isObject(b) && Array.isArray(b.required) ? (b.required as string[]) : []))
      : [];
    return [...new Set([...own, ...fromAllOf])];
  });

  // Array whose items are themselves an object worth expanding — exposes the
  // item schema directly so the template needs no cast.
  const arrayItemSchema = computed<Schema | null>(() => {
    const s = props.schema;
    if (s?.type === 'array' && isObject(s.items) && isObject(s.items.properties)) {
      return s.items as Schema;
    }
    return null;
  });

  // Union members that carry their own object shape (rendered nested).
  const unionObjects = computed<Schema[]>(() => {
    const union = props.schema?.anyOf ?? props.schema?.oneOf;
    if (!Array.isArray(union)) return [];
    return union.filter((m) => isObject(m) && isObject(m.properties));
  });

  const canRecurse = computed(() => props.depth < MAX_DEPTH);

  // Root render mode: an unnamed object node lists its properties directly.
  const isRoot = computed(() => props.name === '');

  const rowKeyClass =
    'font-mono text-[12px] text-day-ink dark:text-night-ink';
  const typeClass =
    'font-mono text-[11px] text-brand-600 dark:text-brand-400';
</script>

<template>
  <!-- Root object: list its properties without a wrapping row -->
  <ul
    v-if="isRoot && objectProperties && canRecurse"
    class="space-y-2">
    <JsonSchemaView
      v-for="(child, key) in objectProperties"
      :key="key"
      :schema="child"
      :name="key"
      :required="requiredKeys.includes(key)"
      :depth="depth + 1" />
  </ul>

  <!-- Root array of objects: label the array, then expand the item shape -->
  <div v-else-if="isRoot && arrayItemSchema && canRecurse">
    <p class="font-mono text-[12px] text-day-ink-muted dark:text-night-ink-muted">
      <span :class="typeClass">{{ label }}</span>
    </p>
    <ul class="mt-1.5 space-y-2">
      <JsonSchemaView
        :schema="arrayItemSchema"
        name="items[]"
        :depth="depth + 1" />
    </ul>
  </div>

  <!-- Root that is not an expandable object: single summary line -->
  <p
    v-else-if="isRoot"
    class="font-mono text-[12px] text-day-ink-muted dark:text-night-ink-muted">
    <span :class="typeClass">{{ label }}</span>
    <template v-if="enumValues">
      : {{ enumValues.map((v) => JSON.stringify(v)).join(' | ') }}
    </template>
  </p>

  <!-- Named field -->
  <li
    v-else
    class="border-l border-day-line pl-3 dark:border-white/[0.09]">
    <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span :class="rowKeyClass">{{ name }}</span>
      <span :class="typeClass">{{ label }}</span>
      <span
        v-if="required"
        class="font-mono text-[9px] tracking-[0.08em] text-rose-600 uppercase dark:text-rose-400">
        {{ $t('web.docs.required') }}
      </span>
    </div>

    <p
      v-if="description"
      class="mt-0.5 text-[12px] leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
      {{ description }}
    </p>

    <p
      v-if="enumValues"
      class="mt-0.5 font-mono text-[11px] break-words text-day-ink-faint dark:text-night-ink-faint">
      {{ enumValues.map((v) => JSON.stringify(v)).join(' · ') }}
    </p>
    <p
      v-else-if="constValue !== undefined"
      class="mt-0.5 font-mono text-[11px] text-day-ink-faint dark:text-night-ink-faint">
      = {{ JSON.stringify(constValue) }}
    </p>

    <!-- Nested object properties -->
    <ul
      v-if="objectProperties && canRecurse"
      class="mt-1.5 space-y-2">
      <JsonSchemaView
        v-for="(child, key) in objectProperties"
        :key="key"
        :schema="child"
        :name="key"
        :required="requiredKeys.includes(key)"
        :depth="depth + 1" />
    </ul>

    <!-- Array item object -->
    <ul
      v-if="arrayItemSchema && canRecurse"
      class="mt-1.5 space-y-2">
      <JsonSchemaView
        :schema="arrayItemSchema"
        name="items[]"
        :depth="depth + 1" />
    </ul>

    <!-- Union member objects -->
    <ul
      v-if="unionObjects.length && canRecurse"
      class="mt-1.5 space-y-2">
      <JsonSchemaView
        v-for="(member, i) in unionObjects"
        :key="i"
        :schema="member"
        :name="`option ${i + 1}`"
        :depth="depth + 1" />
    </ul>
  </li>
</template>
